import {
  INITIAL_ADDRESS_VALUES,
  setDeliveryDetailsSchemaShape,
} from "@fero/components";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import type { Incoterms, ShippingDelivery } from "adl-gen/ferovinum/app/db";
import type { DeliveryOptionReq } from "adl-gen/ferovinum/app/deliveryoptions";
import { parseLocalTimeToDate } from "utils/date-utils";
import { mixed, object, string } from "yup";
import type { DeliveryType } from "../organisation-repurchase-delivery-page";
import type {
  AddressForm,
  DeliveryFormValues,
} from "../organisation-repurchase-delivery-page-view";

export const OUT_OF_BOND_DELIVERY_INCOTERMS: Incoterms[] = ["DDP", "DAP"];
export const IN_BOND_DELIVERY_INCOTERMS: Incoterms[] = ["EXW", "FOB", "CFR"];

const createAddress = (v: ShippingDelivery): AddressForm => ({
  deliveryPointName: v.deliveryPointName ?? "",
  streetName: v.streetName ?? "",
  addressLine1: v.addressLine1 ?? "",
  addressLine2: v.addressLine2 ?? "",
  town: v.town ?? "",
  postCode: v.postCode ?? "",
  country: v.country,
  state: v.state,
  deliveryContact: v.deliveryContact ?? "",
  deliveryContactEmail: v.deliveryContactEmail ?? "",
  deliveryContactNumber: v.deliveryContactNumber ?? "",
  deliveryInstructions: v.deliveryInstructions ?? "",
  deliveryDate: v.deliveryDate ? new Date(v.deliveryDate) : null,
  deliveryTimeEarliest: v.deliveryTimeEarliest
    ? parseLocalTimeToDate(v.deliveryTimeEarliest)
    : null,
  deliveryTimeLatest: v.deliveryTimeLatest
    ? parseLocalTimeToDate(v.deliveryTimeLatest)
    : null,
  deliveryIncoterms: v.deliveryIncoterms ?? null,
  bonded: v.bonded,
});

export function getInitialFormValues(
  initialDeliveryReq: DeliveryOptionReq,
  storageLocation: StorageLocationView,
): DeliveryFormValues {
  let selectedType: DeliveryType = "warehouse";
  let accountCode = "";
  let siteCode = storageLocation.siteCode ?? "";
  let savedAddress = INITIAL_ADDRESS_VALUES as AddressForm;
  let customerAddress = INITIAL_ADDRESS_VALUES as AddressForm;
  switch (initialDeliveryReq.kind) {
    case "inWarehouseTransfer":
      break;
    case "inWarehouseTransferWithAccountCode":
      accountCode = initialDeliveryReq.value.accountCode;
      siteCode = initialDeliveryReq.value.siteCode ?? "";
      break;
    case "orgLocationDelivery":
      selectedType = "savedDeliveryLocation";
      savedAddress = createAddress(initialDeliveryReq.value);
      break;
    case "shippedDelivery":
      selectedType = "customerLocation";
      customerAddress = createAddress(initialDeliveryReq.value);
      break;
    default:
      throw new Error(
        `Invalid delivery option type for repurchase: [${initialDeliveryReq.kind}]`,
      );
  }
  return {
    selectedType,
    accountCode,
    siteCode,
    savedAddress,
    customerAddress,
  };
}

export const createValidationSchema = (
  storageLocation: StorageLocationView,
  needsInternalRefrence: boolean,
) =>
  object().shape({
    selectedType: mixed<DeliveryType>()
      .oneOf(["warehouse", "savedDeliveryLocation", "customerLocation"])
      .required(),
    accountCode: string()
      .trim()
      .when("selectedType", {
        is: (selectedType: DeliveryType) =>
          selectedType === "warehouse" &&
          storageLocation.requiresAccountCodeForInWarehouseTransfer,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    siteCode: string().trim().optional(),
    savedAddress: object()
      .when("selectedType", {
        is: "savedDeliveryLocation",
        then: (schema) =>
          setDeliveryDetailsSchemaShape(
            schema,
            storageLocation.deliveryInstructionConfig,
            [...OUT_OF_BOND_DELIVERY_INCOTERMS, ...IN_BOND_DELIVERY_INCOTERMS],
          ),
        otherwise: (schema) => schema.optional(),
      })
      .optional(),
    customerAddress: object()
      .when("selectedType", {
        is: "customerLocation",
        then: (schema) =>
          setDeliveryDetailsSchemaShape(
            schema,
            storageLocation.deliveryInstructionConfig,
            OUT_OF_BOND_DELIVERY_INCOTERMS,
          ),
        otherwise: (schema) => schema.optional(),
      })
      .optional(),
    internalReference: needsInternalRefrence
      ? string().required()
      : string().optional(),
  });
